import { AIFI_LEVELS } from '@/domains/pages/ai-proficiency/constants';

/** First bucket index where at least one proficiency tier has users. */
export function findLeadingEmptyTrendBucketCount(
  bucketCount: number,
  levelCounts: Record<string, number[]>,
): number {
  for (let index = 0; index < bucketCount; index++) {
    const totalUsers = AIFI_LEVELS.reduce(
      (sum, level) => sum + (levelCounts[level]?.[index] ?? 0),
      0,
    );
    if (totalUsers > 0) {
      return index;
    }
  }
  return bucketCount;
}

function sliceFromIndex<T>(values: T[], startIndex: number): T[] {
  return startIndex > 0 ? values.slice(startIndex) : values;
}

export type TrimmedChartTrendData = {
  startIndex: number;
  buckets: string[];
  mix: Record<string, number[]>;
  counts: Record<string, number[]>;
  orgScores?: Array<number | null>;
  segmentScoresByName?: Record<string, Array<number | null>>;
};

/** Drop leading buckets with zero users across all proficiency tiers. */
export function trimLeadingEmptyChartTrendData(params: {
  buckets: string[];
  mix: Record<string, number[]>;
  counts: Record<string, number[]>;
  orgScores?: Array<number | null>;
  segmentScoresByName?: Record<string, Array<number | null>>;
}): TrimmedChartTrendData {
  const startIndex = findLeadingEmptyTrendBucketCount(params.buckets.length, params.counts);
  if (startIndex === 0) {
    return { startIndex: 0, ...params };
  }

  const buckets = sliceFromIndex(params.buckets, startIndex);
  const mix = Object.fromEntries(
    Object.entries(params.mix).map(([key, values]) => [key, sliceFromIndex(values, startIndex)]),
  );
  const counts = Object.fromEntries(
    Object.entries(params.counts).map(([key, values]) => [key, sliceFromIndex(values, startIndex)]),
  );
  const orgScores = params.orgScores ? sliceFromIndex(params.orgScores, startIndex) : undefined;
  const segmentScoresByName = params.segmentScoresByName
    ? Object.fromEntries(
        Object.entries(params.segmentScoresByName).map(([name, scores]) => [
          name,
          sliceFromIndex(scores, startIndex),
        ]),
      )
    : undefined;

  return { startIndex, buckets, mix, counts, orgScores, segmentScoresByName };
}
